import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { Header as PageHeader } from 'components/Header';
import { Skeleton } from 'components/_shared/Skeleton';
import { Button } from 'components/_shared/Button';
import { ROUTES } from '_constants';
import {
  selectCanReviewStones,
  useGetReviewQueueQuery,
  useReviewStoneMutation
} from 'state';
import {
  ReviewHeader,
  StatusFilter,
  QueueList,
  StoneViewer,
  BulkBar
} from './components';
import { PAGE_SIZE, REVIEW_STATUS } from './constants';
import {
  PageContainer,
  Layout,
  Side,
  Center,
  PageMessage,
  RetryRow
} from './StoneReview.styles';

export const StoneReview = () => {
  const canReview = useSelector(selectCanReviewStones);
  const [field, setField] = useState(null);
  const [statusFilter, setStatusFilter] = useState(REVIEW_STATUS.pending);
  const [page, setPage] = useState(1);
  const [current, setCurrent] = useState(0);
  const [reviewed, setReviewed] = useState(0);
  const [saveFailed, setSaveFailed] = useState(false);

  const { currentData, isError, refetch } = useGetReviewQueueQuery(
    { field: field?.id, status: statusFilter, page },
    { skip: !canReview }
  );
  const [reviewStone, { isLoading: isSaving }] = useReviewStoneMutation();

  const stones = currentData?.results ?? [];
  const selected = stones[Math.min(current, stones.length - 1)];

  useEffect(() => {
    if (isError && page > 1) setPage(1);
  }, [isError, page]);

  useEffect(() => {
    if (current > 0 && current >= stones.length)
      setCurrent(Math.max(stones.length - 1, 0));
  }, [current, stones.length]);

  if (!canReview) return <Redirect to={ROUTES.ROOT} />;

  const pickField = next => {
    setField(next);
    setPage(1);
    setCurrent(0);
  };

  const changeStatus = next => {
    setStatusFilter(next);
    setPage(1);
    setCurrent(0);
  };

  const changePage = nextPage => {
    setPage(nextPage);
    setCurrent(0);
  };

  const decide = review => {
    if (!selected || isSaving || selected.review === review) return;
    setSaveFailed(false);
    reviewStone({ id: selected.id, review })
      .unwrap()
      .then(() => setReviewed(count => count + 1))
      .catch(() => setSaveFailed(true));
  };

  const goPrev = () => setCurrent(index => Math.max(index - 1, 0));
  const goNext = () => setCurrent(index => Math.min(index + 1, stones.length - 1));

  const renderQueue = () => {
    if (!currentData) return <Skeleton height='360px' />;
    if (stones.length === 0) {
      return (
        <PageMessage>
          {statusFilter === REVIEW_STATUS.pending
            ? 'No stones are waiting for review.'
            : 'No stones here.'}
        </PageMessage>
      );
    }
    return (
      <QueueList
        stones={stones}
        current={current}
        onSelect={setCurrent}
        page={page}
        pageSize={PAGE_SIZE}
        pageCount={Math.ceil(currentData.count / PAGE_SIZE)}
        onPageChange={changePage}
      />
    );
  };

  const renderBody = () => {
    if (isError) {
      return (
        <RetryRow>
          <span>Failed to load.</span>
          <Button onClick={refetch}>Retry</Button>
        </RetryRow>
      );
    }
    return (
      <Layout>
        <Side>
          <StatusFilter status={statusFilter} onChange={changeStatus} />
          {renderQueue()}
        </Side>
        <Center>
          {selected && (
            <StoneViewer
              stone={selected}
              onPickField={pickField}
              onPrev={goPrev}
              onNext={goNext}
              onConfirm={() => decide('confirmed')}
              onReject={() => decide('rejected')}
              disablePrev={current === 0}
              disableNext={current >= stones.length - 1}
              loading={isSaving}
              saveFailed={saveFailed}
            />
          )}
        </Center>
      </Layout>
    );
  };

  return (
    <div>
      <PageHeader />
      <PageContainer>
        <ReviewHeader
          count={currentData?.count ?? 0}
          status={statusFilter}
          reviewed={reviewed}
          field={field}
          onClearField={() => pickField(null)}
        />
        <BulkBar
          field={field}
          onApproved={count => setReviewed(total => total + count)}
        />
        {renderBody()}
      </PageContainer>
    </div>
  );
};
